/**
 * metaLeadGen/creativePerformanceData.ts
 *
 * Extended per-creative-type metrics for the 7-section Creative Performance
 * Analysis dashboard. Builds on top of getCreativeFunnel() and adds:
 *   - costPerClick, costPerClosed
 *   - composite score (0-100) and rank
 *   - aggregate totals across all types
 *
 * ARCHITECTURE CONTRACT: Pure data layer. No UI imports.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters } from "./types";
import { getCreativeFunnel } from "./creativeBreakdown";
import type { CreativeFunnelRow } from "./creativeBreakdown";
import { safeDivide } from "./calculations";
import type { CloseCreativeMap } from "./useCloseCreativeAttribution";

// ─────────────────────────────────────────────
// EXTENDED ROW TYPE
// ─────────────────────────────────────────────
export interface CreativePerformanceRow extends CreativeFunnelRow {
  // Extra cost efficiency
  costPerClick:  number;   // spend / clicks   (0 if no clicks)
  costPerClosed: number;   // spend / closed   (0 if no closed)

  // Drop-off rates per transition (% that do NOT proceed)
  dropImprToClicks: number;  // (1 - ctr/100) * 100  → approx ~98–99%
  dropClicksToOptin:number;  // (1 - optinRate/100) * 100
  dropOptinToApp:   number;  // (1 - appRate/100)    * 100
  dropAppToClose:   number;  // (1 - closeRate/100)  * 100

  // Composite score and rank
  score: number;   // 0–100 normalized weighted score
  rank:  number;   // 1 = best, 5 = worst
}

/** Aggregate totals across all creative types (for Section 1 KPI + Section 2 funnel) */
export interface CreativeAggregateTotals {
  impressions:  number;
  clicks:       number;
  optins:       number;
  applications: number;
  closed:       number;
  spend:        number;

  ctr:          number;  // %
  optinRate:    number;  // %
  appRate:      number;  // %
  closeRate:    number;  // %

  costPerOptin: number;
  costPerApp:   number;
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function normalize(val: number, min: number, max: number): number {
  if (max === min) return 50;
  return Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Returns CreativePerformanceRow[] — one per creative type in canonical order.
 * Types with zero impressions get score=0, rank=last.
 */
export function getCreativePerformanceData(
  rows:    MetaLeadGenRow[],
  filters: MetaLeadGenFilters,
  closeAttribution?: CloseCreativeMap,
): CreativePerformanceRow[] {
  const base = getCreativeFunnel(rows, filters, closeAttribution);

  // Extend with extra fields
  const extended: CreativePerformanceRow[] = base.map(r => ({
    ...r,
    costPerClick:      safeDivide(r.spend, r.clicks),
    costPerClosed:     safeDivide(r.spend, r.closed),
    dropImprToClicks:  r.impressions > 0 ? (1 - safeDivide(r.clicks, r.impressions)) * 100 : 0,
    dropClicksToOptin: r.clicks       > 0 ? (1 - safeDivide(r.optins, r.clicks))       * 100 : 0,
    dropOptinToApp:    r.optins       > 0 ? (1 - safeDivide(r.applications, r.optins)) * 100 : 0,
    dropAppToClose:    r.applications > 0 ? (1 - safeDivide(r.closed, r.applications)) * 100 : 0,
    score: 0,
    rank:  0,
  }));

  // Compute composite score for active rows only
  const active = extended.filter(r => r.impressions > 0);
  if (active.length > 0) {
    const ctrVals     = active.map(r => r.ctr);
    const appVals     = active.map(r => r.appRate);
    const closeVals   = active.map(r => r.closeRate);
    const cpaVals     = active.map(r => r.costPerApp).filter(v => v > 0);

    const ctrRange   = { min: Math.min(...ctrVals),   max: Math.max(...ctrVals) };
    const appRange   = { min: Math.min(...appVals),   max: Math.max(...appVals) };
    const closeRange = { min: Math.min(...closeVals), max: Math.max(...closeVals) };
    const cpaRange   = cpaVals.length > 1
      ? { min: Math.min(...cpaVals), max: Math.max(...cpaVals) }
      : { min: 0, max: 1 };

    for (const row of extended) {
      if (row.impressions === 0) continue;
      const ctrScore   = normalize(row.ctr,          ctrRange.min,   ctrRange.max);
      const appScore   = normalize(row.appRate,       appRange.min,   appRange.max);
      const closeScore = normalize(row.closeRate,     closeRange.min, closeRange.max);
      // CPA: lower is better → invert
      const cpaScore   = row.costPerApp > 0
        ? 100 - normalize(row.costPerApp, cpaRange.min, cpaRange.max)
        : 50;

      row.score = Math.round(
        ctrScore   * 0.25 +
        appScore   * 0.30 +
        closeScore * 0.25 +
        cpaScore   * 0.20
      );
    }

    // Assign ranks (higher score = lower rank number)
    const sorted = [...extended].sort((a, b) => b.score - a.score);
    for (const row of extended) {
      row.rank = sorted.findIndex(r => r.type === row.type) + 1;
    }
  }

  return extended;
}

/**
 * Aggregates all CreativePerformanceRow totals into a single KPI object.
 */
export function getCreativeAggregateTotals(
  rows: CreativePerformanceRow[]
): CreativeAggregateTotals {
  const impressions  = rows.reduce((s, r) => s + r.impressions, 0);
  const clicks       = rows.reduce((s, r) => s + r.clicks, 0);
  const optins       = rows.reduce((s, r) => s + r.optins, 0);
  const applications = rows.reduce((s, r) => s + r.applications, 0);
  const closed       = rows.reduce((s, r) => s + r.closed, 0);
  const spend        = rows.reduce((s, r) => s + r.spend, 0);

  return {
    impressions, clicks, optins, applications, closed, spend,
    ctr:          safeDivide(clicks,       impressions)  * 100,
    optinRate:    safeDivide(optins,        clicks)       * 100,
    appRate:      safeDivide(applications,  optins)       * 100,
    closeRate:    safeDivide(closed,        applications) * 100,
    costPerOptin: safeDivide(spend,         optins),
    costPerApp:   safeDivide(spend,         applications),
  };
}
